import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  entities as entitiesApi,
  ontologies as ontologiesApi,
  relationships as relApi,
  type ApiEntity,
} from "@/lib/api";
import { useWorkspaces } from "@/store/workspaces";

/**
 * Modal that runs when the operator finishes drawing a shape on the map.
 * Collects a name + type + optional entity attachments, then persists as
 * an Entity whose `operational_area` carries the drawn GeoJSON geometry.
 * If the operator picked entities to attach, we also POST relationships
 * from the new zone to each of them (default type: "located_in", falling
 * back to whatever relationship type exists in the workspace ontology).
 *
 * Kept intentionally in-map (not a portal / not full-screen) — the
 * drawing is still visible behind, so the operator can double-check
 * geometry before committing.
 */
export function CreateZoneModal({
  workspaceId,
  geometry,
  centroid,
  onClose,
  onCreated,
}: {
  workspaceId: string;
  /** Drawn shape for a zone. Pass `null` to create a **point of interest**
   *  instead: same entity, but only the `centroid` lat/lng is persisted and
   *  no `operational_area` is set. Lets the right-click "Add point of
   *  interest" flow reuse this modal wholesale — name / type / attachment
   *  pickers are identical — instead of duplicating it. */
  geometry:
    | GeoJSON.Polygon
    | GeoJSON.MultiPolygon
    | GeoJSON.LineString
    | GeoJSON.MultiLineString
    | null;
  centroid: [number, number]; // [lng, lat] auto-computed by the caller
  onClose: () => void;
  onCreated: (entity: ApiEntity) => void;
}) {
  const isPoint = geometry === null;
  const qc = useQueryClient();
  const ws = useWorkspaces().getById(workspaceId);
  const [name, setName] = useState("");
  const [type, setType] = useState<string>("");
  const [attachedIds, setAttachedIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Fetch the workspace's ontology (if any) to populate the type dropdown.
  const ontologyQuery = useQuery({
    queryKey: ["ontology", ws?.ontology_id],
    queryFn: () =>
      ws?.ontology_id ? ontologiesApi.get(ws.ontology_id) : Promise.resolve(null),
    enabled: Boolean(ws?.ontology_id),
  });

  // Also fetch entities — for the "attach to" multiselect + fallback type list.
  const entitiesQuery = useQuery({
    queryKey: ["entities", workspaceId],
    queryFn: () => entitiesApi.list(workspaceId),
    enabled: Boolean(workspaceId),
  });

  // Type options: prefer ontology entity_types; fall back to the set of
  // distinct types already present in the workspace. The operator can also
  // free-type if the workspace has no ontology (validation lets anything
  // through in that case — see services.ontology.validate_entity_type).
  const typeOptions: string[] = useMemo(() => {
    const fromOntology = (ontologyQuery.data?.entity_types ?? []).map(
      (t) => t.name,
    );
    if (fromOntology.length > 0) return fromOntology;
    const uniq = new Set<string>();
    for (const e of entitiesQuery.data ?? []) uniq.add(e.type);
    return [...uniq].sort();
  }, [ontologyQuery.data, entitiesQuery.data]);

  // Preselect the most "zone-like" type from the options once they load, so
  // the operator rarely has to interact with the dropdown. useEffect (not
  // useMemo) because it's a side effect — React discards memos on demand
  // and warns loudly about setState inside them under strict mode.
  useEffect(() => {
    if (type || typeOptions.length === 0) return;
    const preferOrder = [
      "zone",
      "area",
      "region",
      "territory",
      "municipality",
      "location",
    ];
    const preferred = preferOrder
      .map((p) => typeOptions.find((t) => t.toLowerCase().includes(p)))
      .find(Boolean);
    setType(preferred ?? typeOptions[0]);
  }, [typeOptions, type]);

  const create = useMutation({
    mutationFn: async () => {
      // 1. Create the zone entity.
      const zone = await entitiesApi.create(workspaceId, {
        type: type.trim(),
        name: name.trim(),
        source: "manual",
        // Backend accepts flat lat/lng OR nested geolocation; use nested to
        // stay explicit. Centroid gives the map something to fly-to when
        // opened via the entity list.
        geolocation: {
          lat: centroid[1],
          lng: centroid[0],
          region: null,
        },
        operational_area: geometry,
      } as Partial<ApiEntity> & { type: string; name: string });

      // 2. If the operator attached other entities, POST relationships.
      // Best-effort: a single failed relationship shouldn't roll back the
      // zone — the operator can retry from the SlideOver's Relationships
      // panel (once we build it). Uses "located_in" if the ontology
      // permits it; otherwise falls back to the first relationship type
      // whose source_types includes this zone's type.
      if (attachedIds.length > 0) {
        const ontology = ontologyQuery.data;
        const relType = pickRelationshipType(ontology, type);
        for (const targetId of attachedIds) {
          try {
            await relApi.create(workspaceId, {
              type: relType,
              source_entity_id: zone.id,
              target_entity_id: targetId,
            });
          } catch (e) {
            // Don't fail the whole save — surface as warning but continue.
            console.warn("Zone attach failed for entity", targetId, e);
          }
        }
      }

      return zone;
    },
    onSuccess: (zone) => {
      qc.invalidateQueries({ queryKey: ["entities", workspaceId] });
      qc.invalidateQueries({ queryKey: ["relationships", workspaceId] });
      onCreated(zone);
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const canSave =
    name.trim().length > 0 && type.trim().length > 0 && !create.isPending;

  return (
    <div className="absolute inset-0 z-30 grid place-items-center pointer-events-none">
      {/* Backdrop is subtle — the operator needs to still see the drawing. */}
      <div
        className="absolute inset-0 bg-black/25 pointer-events-auto"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative pointer-events-auto w-[420px] max-w-[92vw] rounded-lg border border-border bg-background shadow-2xl">
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <div className="label-eyebrow">
              {isPoint ? "New point of interest" : "New zone"}
            </div>
            <div className="text-[13px] font-semibold">
              {isPoint ? (
                <span className="font-mono text-[12px] text-foreground/90">
                  {centroid[1].toFixed(6)}, {centroid[0].toFixed(6)}
                </span>
              ) : (
                "Save the shape you just drew"
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-surface-elevated hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="p-4 space-y-3">
          {error && (
            <div className="rounded-md border border-risk-high/40 bg-risk-high/10 px-3 py-2 text-[12px] text-risk-high">
              {error}
            </div>
          )}

          <label className="block">
            <div className="text-[10.5px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">
              Name <span className="text-risk-high">*</span>
            </div>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Perímetro Cauca 2026-Q3"
              className="w-full rounded-md border border-border bg-surface px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </label>

          <label className="block">
            <div className="text-[10.5px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">
              Type <span className="text-risk-high">*</span>
            </div>
            {typeOptions.length > 0 ? (
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full rounded-md border border-border bg-surface px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {typeOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            ) : (
              <input
                value={type}
                onChange={(e) => setType(e.target.value)}
                placeholder="e.g. zone, area, region"
                className="w-full rounded-md border border-border bg-surface px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
              />
            )}
            <div className="mt-1 text-[10.5px] text-muted-foreground">
              {ontologyQuery.data?.entity_types.length
                ? "Types come from this workspace's ontology."
                : entitiesQuery.data?.length
                  ? "Types listed are the ones already used in this workspace."
                  : "No ontology assigned — any type is allowed."}
            </div>
          </label>

          <AttachEntities
            entities={entitiesQuery.data ?? []}
            selectedIds={attachedIds}
            onChange={setAttachedIds}
          />

          <div className="pt-1 flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="text-[11.5px] text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
            <button
              onClick={() => create.mutate()}
              disabled={!canSave}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-[12px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90"
            >
              {create.isPending && (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              )}
              Save zone
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Attach-to-entities multiselect ----------

function AttachEntities({
  entities,
  selectedIds,
  onChange,
}: {
  entities: ApiEntity[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const [filter, setFilter] = useState("");
  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const list = f
      ? entities.filter(
          (e) =>
            e.name.toLowerCase().includes(f) || e.type.toLowerCase().includes(f),
        )
      : entities;
    // Selected first (so the operator can always see current selection),
    // then by name.
    const sel = new Set(selectedIds);
    return [...list].sort((a, b) => {
      const aSel = sel.has(a.id);
      const bSel = sel.has(b.id);
      if (aSel !== bSel) return aSel ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [entities, selectedIds, filter]);

  const toggle = (id: string) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id],
    );
  };

  return (
    <div>
      <div className="text-[10.5px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">
        Attach to entities (optional)
      </div>
      <input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter entities…"
        className="w-full mb-1.5 rounded-md border border-border bg-surface px-2 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary"
      />
      <div className="max-h-[160px] overflow-y-auto scrollbar-thin rounded-md border border-border bg-surface">
        {filtered.length === 0 ? (
          <div className="p-2 text-[11.5px] text-muted-foreground italic">
            {entities.length === 0 ? "No entities in this workspace yet." : "No matches."}
          </div>
        ) : (
          filtered.slice(0, 100).map((e) => {
            const checked = selectedIds.includes(e.id);
            return (
              <label
                key={e.id}
                className={`flex items-center gap-2 px-2 py-1 text-[12px] cursor-pointer hover:bg-surface-elevated ${
                  checked ? "bg-primary/10" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(e.id)}
                  className="rounded"
                />
                <span className="flex-1 truncate">{e.name}</span>
                <span className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider shrink-0">
                  {e.type}
                </span>
              </label>
            );
          })
        )}
      </div>
      {filtered.length > 100 && (
        <div className="mt-1 text-[10.5px] text-muted-foreground">
          Showing first 100 — refine the filter to see more.
        </div>
      )}
      {selectedIds.length > 0 && (
        <div className="mt-1 text-[11px] text-primary">
          {selectedIds.length} selected
        </div>
      )}
    </div>
  );
}

// ---------- Helpers ----------

/**
 * Pick a plausible relationship type from the ontology for attaching a
 * zone to another entity. Preference order matches how a human would
 * describe the semantic; falls back to the first defined type whose
 * source_types allows the zone's own type. Ultimate fallback: "located_in".
 */
function pickRelationshipType(
  ontology: { relationship_types: { name: string; source_types?: string[] }[] } | null | undefined,
  zoneType: string,
): string {
  const preferred = ["located_in", "contains", "in_zone", "operates_in"];
  const relTypes = ontology?.relationship_types ?? [];
  for (const p of preferred) {
    const hit = relTypes.find((r) => r.name === p);
    if (hit) return p;
  }
  // Fall back to any relationship type this zone's entity type is allowed
  // to be a source of.
  const allowed = relTypes.find((r) =>
    (r.source_types ?? []).includes(zoneType),
  );
  if (allowed) return allowed.name;
  // Absolute fallback — safe even if the workspace has no ontology (which
  // means relationship validation is off anyway).
  return "located_in";
}
